import React, { useState } from "react";
import { ModalX, PageHeader } from "../../components";
import { Button, Checkbox, IconButton, Input, Option, Select, Spinner } from "@material-tailwind/react";
import { Edit2Icon } from "lucide-react";
import moment from "moment";
import { convertToPresentableText, getOrdinal, NotifyError } from "../../utils/functions";
import { LEVELS, LEVELS_ENUM, OPERATIONAL_STATUSES, OPERATIONAL_STATUSES_ENUM, SECURITY_TYPES, SECURITY_TYPES_ENUM } from "../../utils/enums";
import { useGetFloors } from "../../custom-hooks/use-floors";
import { useAddCoworkingSpace, useGetCoworkingSpaces, useUpdateCoworkingSpace } from "../../custom-hooks/use-coworking-spaces";
import { InterpretOperationalStatus } from "../../utils/ui-interpreters";
import { usePermissions } from "../../custom-hooks/use-permissions";


const INITIAL_FORM_DATA = {
    floor: '',
    number: '',
    code: '',
    // capacity: '',
    useableArea: '',
    totalArea: '',
    status: 'operational',
    securityType: '',
    securityLevel: '',
    isAccessible: true,
    isBookable: true,
    dedicatedClimateControl: true,
    dedicatedInternet: false
}
const CoworkingSpacePage = () => {

    // State variables
    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedSpaceId, setSelectedSpaceId] = useState(null);
    // const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
    // const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);

    const { data: floors, isLoading: isFloorsLoading, isError: isFloorsError } = useGetFloors();
    const { data: spaces, isLoading: isSpacesLoading, isError: isSpacesError } = useGetCoworkingSpaces();

    const addCoworkingSpaceMutation = useAddCoworkingSpace();
    const updateCoworkingSpaceMutation = useUpdateCoworkingSpace();

    const { can } = usePermissions();


    // Handle form input change
    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckBoxChange = async (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    // Handle form submission
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isUpdating) {
                // Update building logic
                const updatedSpace = {
                    floor: formData.floor,
                    number: formData.number,
                    code: formData.code,
                    // capacity: formData.capacity,
                    useableArea: formData.useableArea,
                    totalArea: formData.totalArea,
                    status: formData.status,
                    securityType: formData.securityType,
                    securityLevel: formData.securityLevel,
                    isAccessible: formData.isAccessible,
                    isBookable: formData.isBookable,
                    dedicatedClimateControl: formData.dedicatedClimateControl,
                    dedicatedInternet: formData.dedicatedInternet,
                };
                await updateCoworkingSpaceMutation.mutateAsync({ coworkingSpaceId: selectedSpaceId, data: updatedSpace });
            } else {
                // Add new building logic
                const newSpace = {
                    floor: formData.floor,
                    number: formData.number,
                    code: formData.code,
                    // capacity: formData.capacity,
                    useableArea: formData.useableArea,
                    totalArea: formData.totalArea,
                    status: formData.status,
                    securityType: formData.securityType,
                    securityLevel: formData.securityLevel,
                    isAccessible: formData.isAccessible,
                    isBookable: formData.isBookable,
                    dedicatedClimateControl: formData.dedicatedClimateControl,
                    dedicatedInternet: formData.dedicatedInternet,
                };
                await addCoworkingSpaceMutation.mutateAsync({ floorId: formData.floor, data: newSpace });

            }
        } catch (error) {
            console.log('error', error);
            NotifyError(error.response.data.message, error);
        } finally {

            setModalOpen(false);
            setFormData(INITIAL_FORM_DATA);
            setIsUpdating(false);
            setSelectedSpaceId(null);
        }
    };

    // Open modal for adding a new building
    const openAddSpaceModal = () => {
        setModalOpen(true);
        const defaultValueForInitialFormData = { ...INITIAL_FORM_DATA };
        defaultValueForInitialFormData.status = OPERATIONAL_STATUSES.OPERATIONAL;
        defaultValueForInitialFormData.securityType = SECURITY_TYPES.SHARED_FINGERPRINT;
        defaultValueForInitialFormData.securityLevel = LEVELS.MEDIUM;

        setFormData(defaultValueForInitialFormData);
        setIsUpdating(false);
    };

    // Open modal for updating a building
    const openUpdateSpaceModal = (space) => {
        setModalOpen(true);
        setFormData({ ...space, floor: space.floor._id });
        setIsUpdating(true);
        setSelectedSpaceId(space._id);
    };

    return (
        <div className={``}>
            <div className="m-5 p-5 border rounded-3xl">
                <PageHeader
                    title="Coworking Space Management"
                    isLoading={isSpacesLoading}
                    actionOnClick={can('FX9', 'edit') ? openAddSpaceModal : undefined}
                    actionType="add"
                    actionText="Add New Space"
                />
                {
                    isSpacesLoading ?
                        <div className="flex justify-center">
                            <Spinner />
                        </div> :
                        isSpacesError ?
                            <div className="text-center py-5 text-red-500">
                                Error loading spaces. Please try again.
                            </div> :
                            <div className="overflow-x-auto mt-4">
                                <table className="w-full min-w-max table-auto text-left">
                                    <thead className="text-sm">
                                        <tr className="py-3">
                                            <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Working Spaces ({spaces?.length})</th>
                                            {/* <th colSpan={2} className="py-3">
                                    <Input
                                        label="Search"
                                    />
                                </th> */}
                                        </tr>
                                        <tr className="py-3 uppercase">
                                            <th className="py-3">#</th>
                                            <th className="py-3">Seats</th>
                                            <th className="py-3">Status</th>
                                            {/* <th className="py-3">Modified</th> */}
                                            <th className="py-3">Created</th>
                                            {can('FX9', 'edit') && (
                                            <th className="py-3">Actions</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {spaces?.map((space, index) => (
                                            <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                                                <td className="font-thin py-3 text-sm">
                                                    <div className="font-bold">
                                                        {space.number} ({space.code})
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {space.floor.floorCode} - {space.floor.building.name} - {space.floor.building.branch.name}
                                                    </div>
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    {space.seats.length}
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    <div className="flex">
                                                        {InterpretOperationalStatus(space.status)}
                                                    </div>
                                                </td>

                                                <td className="font-thin py-3 text-sm">
                                                    {moment(space.createdDate).format('lll')}
                                                </td>
                                                {can('FX9', 'edit') && (
                                                <td className="flex gap-1 py-3">

                                                    <IconButton
                                                        size="sm"
                                                        color="orange"
                                                        className="rounded-full"
                                                        onClick={() => openUpdateSpaceModal(space)}
                                                    >
                                                        <Edit2Icon size={12} />
                                                    </IconButton>
                                                </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>

                                </table>
                            </div>

                }

                {/* Modal for Add/Update Space */}
                <ModalX isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title={isUpdating ? "Update Space" : "Add New Space"}
                    maxWidth="xxl">
                    <form onSubmit={handleFormSubmit}>
                        <div className="space-y-2">
                            <div >
                                {
                                    isFloorsLoading ? <Spinner /> :
                                        isFloorsError ? <div className="text-center py-5 text-red-500">Error loading floors. Please try again.</div> :
                                            <Select
                                                label={<>Select Floor <span className="text-red-500 ml-1">*</span></>}
                                                value={formData.floor}
                                                disabled={isFloorsLoading}
                                                onChange={(newValue) => handleSelectChange("floor", newValue)}
                                            >
                                                {
                                                    floors?.map((floor, index) => (
                                                        <Option key={index} value={floor._id}>
                                                            <div className="flex justify-between gap-3 w-full">
                                                                <div className="">
                                                                    {getOrdinal(floor.number)} Floor - {floor.floorCode} in ({floor.building.name})
                                                                </div>
                                                                <div className="">
                                                                    {floor.building.branch.name}
                                                                </div>
                                                            </div>
                                                        </Option>
                                                    ))
                                                }
                                            </Select>
                                }
                            </div>
                            <div className="flex gap-2 flex-col md:flex-row">
                                <Input
                                    label="Space Number"
                                    name="number"
                                    value={formData.number}
                                    onChange={handleInputChange}
                                    required
                                />
                                <Input
                                    label="Space Code"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="flex gap-2 flex-col md:flex-row">
                                {/* <Input
                                    label="Person Capacity"
                                    name="capacity"
                                    type="number"
                                    value={formData.capacity}
                                    onChange={handleInputChange}
                                /> */}
                                <Input
                                    label="Usable Area (sq/ft)"
                                    name="useableArea"
                                    type="number"
                                    value={formData.useableArea}
                                    onChange={handleInputChange}
                                />
                                <Input
                                    label="Total Area (sq/ft)"
                                    name="totalArea"
                                    type="number"
                                    value={formData.totalArea}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="flex gap-2 flex-col md:flex-row">
                                <Select
                                    label="Status"
                                    value={formData.status}
                                    onChange={newValue => handleSelectChange("status", newValue)}
                                >
                                    {
                                        OPERATIONAL_STATUSES_ENUM.map((status, index) => (
                                            <Option key={index} value={status}>
                                                {convertToPresentableText(status)}
                                            </Option>
                                        ))
                                    }
                                </Select>
                                <Select
                                    label="Security Level"
                                    value={formData.securityLevel}
                                    onChange={newValue => handleSelectChange("securityLevel", newValue)}
                                >
                                    {
                                        LEVELS_ENUM.map((level, index) => (
                                            <Option key={index} value={level}>
                                                {convertToPresentableText(level)}
                                            </Option>
                                        ))
                                    }
                                </Select>
                                <Select
                                    label="Security Type"
                                    value={formData.securityType}
                                    onChange={newValue => handleSelectChange("securityType", newValue)}
                                >
                                    {
                                        SECURITY_TYPES_ENUM.map((type, index) => (
                                            <Option key={index} value={type}>
                                                {convertToPresentableText(type)}
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </div>

                            {/* <div className="">
                <span className="text-md font-thin text-gray-900">
                  Location
                </span>
              </div> */}
                            {/* <div className="flex gap-4">
                <Input
                  label="Google Maps Link"
                  name="googleMapsLink"
                  value={formData.googleMapsLink}
                  onChange={handleInputChange}
                  icon={formData.googleMapsLink !== "" ? (googleMapsLinkStatus ? <Check size={12} /> : <ShieldAlert size={12} />) : <></>}
                />

                <Input
                  label="Longitude"
                  name="lng"
                  value={formData.lng}
                  onChange={handleInputChange}
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
                <Input
                  label="Latitude"
                  name="lat"
                  value={formData.lat}
                  onChange={handleInputChange}
                  type="number"
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
              </div> */}

                            <div className="">

                                <Checkbox
                                    name="isBookable"
                                    checked={formData.isBookable}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Is Bookable"
                                    onChange={handleCheckBoxChange}
                                />
                                <Checkbox
                                    name="isAccessible"
                                    checked={formData.isAccessible}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Is Accessible"
                                    onChange={handleCheckBoxChange}
                                />
                                <Checkbox
                                    name="dedicatedInternet"
                                    checked={formData.dedicatedInternet}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Dedicated Internet"
                                    onChange={handleCheckBoxChange}
                                />
                                <Checkbox
                                    name="dedicatedClimateControl"
                                    checked={formData.dedicatedClimateControl}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Dedicated Climate Control"
                                    onChange={handleCheckBoxChange}
                                />
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end space-x-2">
                            <Button
                                onClick={() => setModalOpen(false)}
                                className="rounded-full "
                                variant="text"
                            >
                                Cancel
                            </Button>
                            <Button type="submit" className="rounded-full">
                                {isUpdating ? "Update" : "Add"}
                            </Button>
                        </div>
                    </form>
                </ModalX>
            </div>
        </div>
    );
};

export default CoworkingSpacePage;
